import * as Icon from "@iyk/icons"
import * as UI from "@iyk/ui"
import * as Carousel from "./ui/carousel.tsx"

import { each } from "@iyk/flow"
import { Link } from "@remix-run/react"
import { SectionLayout } from "./ui/section-layout.tsx"

export const Testimonials = ({ className, ...props }: UI.HTMLElementProps) => (
  <SectionLayout
    size="fluid"
    theme="light"
    className={classForTestimonials({ className })}
    {...props}
  >
    <div
      className={UI.classForStack({
        className: "bg-white rounded-lg basis-1/2 relative overflow-hidden",
      })}
    >
      <UI.Swirls
        startColor="#0000"
        endColor="#0004"
        className={UI.classForLayer({
          className: "absolute self-center justify-self-center size-[100%] [scale:1.2]",
        })}
      />

      <p
        className={UI.classForLayer({
          className: "flex flex-col gap-4 items-center self-end justify-self-center pt-36",
        })}
      >
        <svg width="49" height="48" fill="none" className="mx-auto" aria-hidden="true">
          <path
            fill="#646464"
            fillRule="evenodd"
            d="M24.5 46.76c4.32 0 8.35-1.2 11.8-3.3V16.15a2.28 2.28 0 0 0-2.3-2.29H15a2.28 2.28 0 0 0-2.28 2.28v27.33a22.65 22.65 0 0 0 11.79 3.29Zm13.03-30.62v26.52a22.76 22.76 0 1 0-26.07 0V16.14A3.52 3.52 0 0 1 15 12.62h19a3.52 3.52 0 0 1 3.52 3.52ZM24.5 48a24 24 0 1 0 0-48 24 24 0 0 0 0 48Zm-5.38-16.97V20.28h10.76v10.75H19.12Zm-1.24-11.17c0-.45.37-.83.83-.83h11.58c.46 0 .83.38.83.83v11.59c0 .46-.37.83-.83.83H18.71a.83.83 0 0 1-.83-.83V19.86Zm4.97 1.86h-2.28v7.87h7.86v-7.87h-4.55v2.86a1.24 1.24 0 1 0 1.24 0v-1.61h2.07v5.37h-5.38v-5.37h1.04v-1.25Z"
            clipRule="evenodd"
          />
        </svg>
        <span>Just tap, no app needed</span>
        <img
          src="https://assets.iyk-dev.app/postcards/just_tap_iyk.webp"
          alt=""
          width="554"
          height="998"
          loading="lazy"
          className="w-[336px] h-[180px] md:h-[352px] object-cover object-top relative z-10 mt-10"
        />
      </p>
    </div>
    <article
      className="basis-2/4 bg-white order-[-1] md:order-1 rounded-lg"
      aria-label="Testimonials"
    >
      <CarouselWithTestimonials />
    </article>
  </SectionLayout>
)

const classForTestimonials = UI.cva([
  "grid [grid-template-rows:1fr_1fr]",
  "md:flex md:flex-row gap-4",
])

const CarouselWithTestimonials = () => {
  return (
    <Carousel.Root options={CAROUSEL_OPTIONS} className="m-auto grid gap-6 py-36 md:py-0 h-full">
      <Carousel.Slides className={{ container: "h-full" }}>
        {each(SLIDES, (slide) => (
          <Carousel.Slide
            key={slide.text}
            className="flex flex-col gap-6 items-center justify-center text-center flex-[0_0_100%]"
          >
            <span className="max-w-[400px] text-xl text-balance px-4">{slide.text}</span>
            {slide.cta && (
              <Link
                target="_blank"
                rel="noreferrer"
                to={slide.cta.href}
                className={UI.classForButton({ size: "sm", variant: "secondary" })}
              >
                {slide.cta.text}
                <slide.cta.icon />
              </Link>
            )}
            <div className="flex flex-col items-center gap-2">
              <UI.Avatar src={slide.avatar.src} alt="" className="size-12" />
              <span className="text-xs-upper">{slide.username}</span>
            </div>
          </Carousel.Slide>
        ))}
      </Carousel.Slides>
      <Carousel.Controls className="md:absolute md:bottom-6 gap-2 md:gap-8 place-self-center" />
    </Carousel.Root>
  )
}

const CAROUSEL_OPTIONS: Carousel.CarouselOptions = {
  loop: true,
}

const SLIDES: Slide[] = [
  {
    text: "A blend of tradition and technology, a way to say something meaningful that they can revisit anytime.",
    username: "MATTIA",
    cta: {
      href: "https://www.instagram.com/reel/DCZCmuvgMvt/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==",
      text: "Watch",
      icon: Icon.Instagram,
    },
    avatar: {
      src: "https://assets.iyk-dev.app/postcards/avatars/avatar-mattia.webp",
      alt: "Mattia",
    },
  },
  {
    text: "A new way of connecting with my friends and family...",
    username: "ASHLEY",
    cta: {
      href: "https://www.instagram.com/reel/DCXFuxRRd3Y/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==",
      text: "Watch",
      icon: Icon.Instagram,
    },
    avatar: {
      src: "https://assets.iyk-dev.app/postcards/avatars/avatar-ashley.webp",
      alt: "Ashley",
    },
  },
  {
    text: "Can’t wait to send this to my bestie",
    username: "ESS",
    cta: {
      href: "https://www.instagram.com/reel/DCRwItONcuT/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==",
      text: "Watch",
      icon: Icon.Instagram,
    },
    avatar: {
      src: "https://assets.iyk-dev.app/postcards/avatars/avatar-ess.webp",
      alt: "Ess",
    },
  },
  {
    text: "Bridging nostalgia and connection. Creating something truly special..",
    username: "LOUIS",
    cta: {
      href: "https://www.instagram.com/reel/DCll9eSphJ4/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA==",
      text: "Watch",
      icon: Icon.Instagram,
    },
    avatar: {
      src: "https://assets.iyk-dev.app/postcards/avatars/avatar-louis.webp",
      alt: "Louis",
    },
  },
  {
    text: "Wait, this is like the future of postcards!!!",
    username: "SASHA",
    avatar: {
      src: "https://assets.iyk-dev.app/postcards/avatars/avatar-sasha.webp",
      alt: "Sasha",
    },
  },
  {
    text: "I have this card on a fridge and tap it whenever I miss my girl’s face",
    username: "FATIMA'S MOM",
    avatar: {
      src: "https://assets.iyk-dev.app/postcards/avatars/avatar-fatimamom.webp",
      alt: "Fatima's mom",
    },
  },
  {
    text: "I can never find a video on my phone, but this card sits on my desk and I rewatch the video of our son’s first steps every day.",
    username: "CHRIS",
    avatar: {
      src: "https://assets.iyk-dev.app/postcards/avatars/avatar-chris.webp",
      alt: "Chris",
    },
  },
  {
    text: "I haven’t mailed a card in ages, but I just went on a trip to New Zealand and sent the card with my secret spots from there to my bestie.",
    username: "ERIC",
    avatar: {
      src: "https://assets.iyk-dev.app/postcards/avatars/avatar-eric.webp",
      alt: "Eric",
    },
  },
]

type Slide = {
  text: string
  username: string
  cta?: {
    href: string
    text: string
    icon: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
  }
  avatar: {
    src: string
    alt: string
  }
}
